import { SetStateAction, useEffect, useState } from 'react';

import { parseAbsoluteToLocal } from '@internationalized/date';
import { ClickAwayListener, Divider, Box, Stack, styled, SxProps, useTheme } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import {
	Button,
	CalendarCell,
	CalendarGrid,
	DateRange,
	DateValue,
	Heading,
	I18nProvider,
	RangeCalendar,
	RangeCalendarProps,
} from 'react-aria-components';
import { useTranslation } from 'react-i18next';

import ActionButton from '~components/buttons/actionButton';
import ActionButtonStyle from '~components/buttons/actionButtonStyle';
import VectorImage from '~components/media/vectorImage';
import { StatusVariant } from '~enums';
import { useSession } from '~hooks';
import { Dimens } from '~themes';

interface DateRangePickerProps<T extends DateValue> extends RangeCalendarProps<T> {
	/**
	 * This prop doesn't do anything in this component, but it needs to be aware to
	 * make the cancel and click outside work properly to reset the selection.
	 */
	isOpen: boolean;
	showClearDates?: boolean;
	showDefaultInputIcon?: boolean;
	minDate?: Dayjs;
	maxDate?: Dayjs;
	initialStartDate?: Dayjs;
	initialEndDate?: Dayjs;
	onDatesChange?: (start: Dayjs, end: Dayjs) => void;
	onCancelled?: () => void;
	errorMessage?: string;
	sx?: SxProps;
	resetTrigger?: number;
}

// TODO: Investigate why isOpen, value, minDate, maxDate are not used and if they should be used.
// Otherwise clean up.
const DateRangePicker = <T extends DateValue>({
	initialStartDate,
	initialEndDate,
	onDatesChange,
	onCancelled,
	sx,
	resetTrigger = 0,
}: DateRangePickerProps<T>) => {
	const { user } = useSession();

	const { palette } = useTheme();
	const { t } = useTranslation();
	const getInitialDateRange = (): DateRange | null =>
		initialStartDate && initialEndDate ?
			{
				start: parseAbsoluteToLocal(initialStartDate.toISOString()),
				end: parseAbsoluteToLocal(initialEndDate.toISOString()),
			}
		:	null;

	const [selectedDateRange, setSelectedDateRange] = useState<DateRange | null>(() =>
		getInitialDateRange()
	);
	const [confirmedDateRange, setConfirmedDateRange] = useState<DateRange | null>(() =>
		getInitialDateRange()
	);

	const resetInput = () => {
		setSelectedDateRange?.(confirmedDateRange ? confirmedDateRange : getInitialDateRange());
	};

	useEffect(() => {
		if (resetTrigger != 0) {
			if (initialStartDate && initialEndDate) {
				onDatesChange?.(initialStartDate, initialEndDate);
			}
			const initialRange = getInitialDateRange();
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setSelectedDateRange?.(initialRange);
		}
	}, [resetTrigger]);

	return (
		<ClickAwayListener
			onClickAway={() => {
				resetInput();
			}}
		>
			<I18nProvider locale={user?.culture ?? 'en-US'}>
				<StyledRangeCalendar
					value={selectedDateRange}
					sx={{
						width: 'fit-content',
						height: {
							xs: '400px',
							md: 'auto',
						},
						overflowX: 'auto',
						background: palette.bgPrimary.main,
						...sx,
					}}
					firstDayOfWeek='mon'
					pageBehavior='single'
					onChange={(value: SetStateAction<DateRange | null>) => {
						setSelectedDateRange(value);
					}}
					visibleDuration={{ months: 2 }}
				>
					<header>
						<Stack direction='row' alignItems='center' justifyContent='space-between' gap={4}>
							<StyledChevronButton slot='previous'>
								<VectorImage name='chevron/left' />
							</StyledChevronButton>
							<Heading />
							<StyledChevronButton slot='next'>
								<VectorImage name='chevron/right' />
							</StyledChevronButton>
						</Stack>
					</header>
					<Stack
						gap={Dimens.spacing2xl}
						sx={{
							flexDirection: {
								xs: 'column',
								md: 'row',
							},
							alignItems: 'center',
						}}
					>
						<CalendarGrid>{(date) => <CalendarCell date={date} />}</CalendarGrid>
						<CalendarGrid offset={{ months: 1 }}>
							{(date) => <CalendarCell date={date} />}
						</CalendarGrid>
					</Stack>
					<Divider sx={{ background: palette.borderTertiary.main }} />
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'end',
							paddingTop: Dimens.spacingMd,
							gap: Dimens.spacingMd,
						}}
					>
						<ActionButton
							label={t('cancel', { ns: 'actions' })}
							style={ActionButtonStyle.Bordered}
							onClick={() => {
								resetInput();
								onCancelled?.();
							}}
						/>
						<ActionButton
							label={t('confirm', { ns: 'actions' })}
							variant={StatusVariant.Brand}
							disabled={selectedDateRange == undefined}
							onClick={() => {
								if (selectedDateRange != undefined) {
									const startDateValue = dayjs(selectedDateRange.start.toDate('utc')).startOf(
										'day'
									);
									const endDateValue = dayjs(selectedDateRange.end.toDate('utc')).endOf('day');
									onDatesChange?.(startDateValue, endDateValue);
									setConfirmedDateRange(selectedDateRange);
								}
							}}
						/>
					</Box>
				</StyledRangeCalendar>
			</I18nProvider>
		</ClickAwayListener>
	);
};

const StyledChevronButton = styled(Button)(({ theme }) => {
	return {
		background: 'transparent',
		height: '40px',
		border: 'none',
		cursor: 'pointer',
		'& svg': {
			height: '24px',
			width: '24px',
			'& path': {
				stroke: theme.palette.borderPrimarySolid.main,
			},
		},
	};
});

const StyledRangeCalendar = styled(RangeCalendar)(({ theme }) => {
	const { typography } = useTheme();
	return {
		'& header': {
			...typography.labelLg,
			textAlign: 'center',
		},
		'& table': {
			borderSpacing: '0',
			borderCollapse: 'separate',
		},
		'& table td': {
			height: '40px',
			overflow: 'hidden',
		},
		'& table thead': {
			'& tr': {
				display: 'table-row',
				height: '40px',
			},
		},
		'& .react-aria-CalendarHeaderCell': {
			...theme.typography.labelLg,
		},
		'& .react-aria-CalendarCell': {
			...theme.typography.labelSm,
			width: '40px',
			height: '40px',
			lineHeight: '40px',
			textAlign: 'center',
			borderRadius: '6px',
			cursor: 'default',
			outline: 'none',
			'&[data-selected]': {
				background: theme.palette.bgTertiary.main,
				borderRadius: 0,
				'&[data-focus-visible]': {
					outlineColor: 'transparent',
					outlineOffset: '-3px',
				},
			},
			'&[data-outside-month]': {
				display: 'none',
			},
			'&[data-focus-visible]': {
				outline: '2px solid transparent',
				outlineOffset: '-2px',
			},
			'&[data-selection-start]': {
				borderRadius: Dimens.radiusFull,
				background: theme.palette.bgBrandSolid.main,
			},
			'&[data-selection-end]': {
				borderRadius: Dimens.radiusFull,
				background: theme.palette.bgBrandSolid.main,
			},
		},
		'& table td:has(.react-aria-CalendarCell[data-selection-end])': {
			position: 'relative',
			zIndex: 1,
		},
		'& table td:has(.react-aria-CalendarCell[data-selection-end])::before': {
			position: 'absolute',
			zIndex: -1,
			height: '40px',
			width: '40px',
			content: '""',
			borderRadius: 0,
			background: theme.palette.bgTertiary.main,
			borderStartEndRadius: Dimens.radiusFull,
			borderEndEndRadius: Dimens.radiusFull,
		},
		'& table td:has(.react-aria-CalendarCell[data-selection-start])': {
			position: 'relative',
			zIndex: 1,
		},
		'& table td:has(.react-aria-CalendarCell[data-selection-start])::before': {
			position: 'absolute',
			zIndex: -1,
			height: '40px',
			width: '40px',
			content: '""',
			borderRadius: 0,
			background: theme.palette.bgTertiary.main,
			borderEndStartRadius: Dimens.radiusFull,
			borderStartStartRadius: Dimens.radiusFull,
		},
		'& table td:has(.react-aria-CalendarCell[data-outside-month])::before': {
			display: 'none',
		},
	};
});

export default DateRangePicker;
